import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEvent, SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntitySchema } from "../../utils/system-entities";
import { refineCreateTaskAssignee } from "./refineCreateTaskAssignee";
import { taskOutputSchema } from "./shared";

/**
 * Create Task Step
 *
 * Creates a new task with optional entity linking.
 * Used as both an agent tool (via ToolCatalog) and a workflow step (via StepRegistry).
 */
export const createTaskInputObject = z.object({
  title: z.string().describe("Title of the task"),
  description: z.string().optional().describe("Detailed task description"),
  priority: z
    .enum(["low", "medium", "high", "urgent"])
    .optional()
    .describe("Task priority (defaults to medium)"),
  assigneeType: z
    .enum(["user", "agent"])
    .optional()
    .describe("Type of assignee"),
  assigneeUserId: z
    .uuid()
    .optional()
    .describe("User ID when assigneeType is user"),
  assigneeAgentDefinitionId: z
    .uuid()
    .optional()
    .describe("Agent definition ID when assigneeType is agent"),
  dueDate: z.iso
    .datetime()
    .optional()
    .describe("Due date as ISO datetime string"),
  metadata: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("Arbitrary metadata to attach to the task"),
  input: z
    .record(z.string(), z.unknown())
    .optional()
    .describe(
      "Typed payload required when the selected task type declares an input schema. Field sockets `taskInput.<name>` overlay this bag.",
    ),
  tags: z.array(z.string()).optional().describe("Tags for the task"),
  watcherEmails: z
    .array(z.string().email())
    .optional()
    .describe(
      "Email addresses whose matching users should watch the task. Unknown addresses are ignored.",
    ),
  watcherUserIds: z
    .array(z.uuid())
    .optional()
    .describe(
      "User ids to add as task watchers. Prefer this over watcherEmails when the upstream step already resolved users.",
    ),
  taskNamespaceId: z
    .uuid()
    .optional()
    .describe(
      "Namespace to create the task in. Overrides the step's configured namespace. A selected task type with a live default namespace still wins; otherwise the org default is used.",
    ),
  sourceEntity: SystemEntitySchema.optional().describe(
    "Entity to link to this task via TASK_EXECUTION association. When the selected task type declares an anchor entity type, this entity is also used as the task's anchor if it matches (and is required unless `anchorEntity` is wired).",
  ),
  sourceEntityType: z
    .nativeEnum(SystemEntity)
    .optional()
    .describe(
      "Entity type to link to this task — an alternative to `sourceEntity` for when you only have a bare entity id (paired with `sourceEntityId`). Ignored if `sourceEntity` is provided.",
    ),
  sourceEntityId: z
    .uuid()
    .optional()
    .describe(
      "Entity id to link to this task — an alternative to `sourceEntity` for when you only have a bare entity id (paired with `sourceEntityType`). Ignored if `sourceEntity` is provided.",
    ),
  taskTypeKey: z
    .string()
    .optional()
    .describe(
      "Org-scoped task type key giving this task typed behavior (anchoring, outcomes, completion gating). Wire from an upstream step; overrides the config default.",
    ),
  anchorEntity: SystemEntitySchema.optional().describe(
    "Business entity to anchor the task to (requires a task type declaring a matching anchorEntityType). When the type declares an anchor, `sourceEntity` may be used instead if it matches. Fails if another open task already holds the (type, anchor) slot — use resolve_task for idempotent attachment.",
  ),
});

/**
 * Task-type metadata copied onto a task step's config by the type picker.
 * Shared by Create Task and Create Typed Task so the socket-driving keys
 * cannot drift. Assignee and namespace are intentionally not here: Create
 * Typed Task must not store them, and Create Task adds its own fields.
 */
export const taskTypeConfigShape = {
  taskTypeKey: z
    .string()
    .trim()
    .min(1)
    .optional()
    .describe(
      "Default org-scoped task type key used when the `taskTypeKey` input is not wired.",
    ),
  taskTypeAnchorEntityType: z
    .nativeEnum(SystemEntity)
    .optional()
    .describe(
      "Copied from the selected task type's declared anchor entity. Drives the sourceEntity input socket (required + typed) so the canvas enforces the type's source-entity constraint. Set by the task-type picker; do not set independently of taskTypeKey.",
    ),
  taskTypeInputFields: z
    .array(
      z.object({
        fieldName: z.string(),
        fieldType: z.string(),
        label: z.string(),
        isRequired: z.boolean(),
        isMultiple: z.boolean(),
      }),
    )
    .optional()
    .describe(
      "Copied from the selected task type's input schema. Drives per-field taskInput.* sockets. Set by the task-type picker; do not set independently of taskTypeKey.",
    ),
};

export const createTaskOutputSchema = z.object({
  task: taskOutputSchema,
  taskId: z
    .uuid()
    .describe(
      "The created (or idempotently reused) task's id (flat, for wiring — e.g. into create_subtask.parentTaskId)",
    ),
  metadata: z
    .record(z.string(), z.unknown())
    .describe(
      "The task's metadata (flat, for wiring — e.g. into create_subtask.metadata so a stage subtask carries the parent's work item). Empty object when the task has none.",
    ),
  created: z
    .boolean()
    .describe(
      "True when a new task was created, false when an existing task was returned via idempotency match",
    ),
  input: z
    .record(z.string(), z.unknown())
    .describe(
      "The task's typed input payload (empty object when the task has none)",
    ),
});

export const createTaskStep = defineStepConfig({
  type: "create_task",
  name: "Create Task",
  description: "Create a new task with optional entity linking",
  category: "Tasks",

  inputSchema: createTaskInputObject.catchall(z.unknown()),
  outputSchema: createTaskOutputSchema,

  configSchema: z
    .object({
      assigneeType: z
        .enum(["user", "agent"])
        .optional()
        .describe("Default assignee type when input assigneeType is not set"),
      assigneeUserId: z
        .uuid()
        .optional()
        .describe(
          "Default user assignee ID when assigneeType is user. Optional when groups/contacts are configured — the first resolved user then becomes the assignee.",
        ),
      assigneeUserGroupIds: z
        .array(z.uuid())
        .optional()
        .describe(
          "User groups whose members are included (same selection as the Notify step). The task is assigned to one user (assigneeUserId when set, else the first resolved member); every other resolved user is added as a watcher.",
        ),
      assigneeContactIds: z
        .array(z.uuid())
        .optional()
        .describe(
          "Address-book (internal) contacts to include. Contacts resolve to users by email; contacts with no matching user are skipped (tasks have no email channel).",
        ),
      assigneeAgentDefinitionId: z
        .uuid()
        .optional()
        .describe(
          "Default agent definition assignee ID when assigneeType is agent",
        ),
      taskNamespaceKey: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe(
          "Namespace key (e.g. 'SHIP') to create the task in. Portable across orgs — resolved to the namespace at run time. Used when the `taskNamespaceId` input is not wired and the selected task type has no default namespace. A type with a default namespace always places the task there.",
        ),
      taskNamespaceId: z
        .uuid()
        .optional()
        .describe(
          "Namespace id to create the task in. Prefer `taskNamespaceKey` in templates. The input socket overrides this field. A selected task type with a live default namespace still wins.",
        ),
      ...taskTypeConfigShape,
    })
    .superRefine(refineCreateTaskAssignee),

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],

  emits: [SystemEvent.TASK_CREATED] as const,
});
